import { useEffect, useRef, useState } from "react";

// Trailing padding (not flex gap) keeps every item the same width wherever it sits,
// so sliding by exactly half the track lands on the seam.
const ITEM_CLASS =
  "shrink-0 whitespace-nowrap pe-10 text-xs font-semibold leading-[21.387px] ltr:font-poppins min-[992px]:pe-[120px] min-[992px]:text-base min-[992px]:leading-[26px]";

const PAUSE_CLASS = "group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused]";

/**
 * Messages that scroll in reading direction (RTL content enters from the left) and loop
 * without a gap. One "set" is every message once; the track holds two identical halves,
 * each with enough sets to fill the strip, and slides by exactly one half.
 */
export default function BannerMarquee({ items, speed, pauseOnHover }) {
  const containerRef = useRef(null);
  const setRef = useRef(null);
  const [loop, setLoop] = useState({ sets: 1, durationS: 0 });
  const itemsKey = items.join("\n");

  useEffect(() => {
    const container = containerRef.current;
    const firstSet = setRef.current;
    if (!container || !firstSet) return undefined;

    const measure = () => {
      const setWidth = firstSet.offsetWidth;
      if (!setWidth) return;
      const sets = Math.max(1, Math.ceil(container.offsetWidth / setWidth));
      setLoop({ sets, durationS: (sets * setWidth) / speed });
    };
    measure();
    // Also watch the set: its width changes once the web font swaps in.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(firstSet);
    return () => observer.disconnect();
  }, [itemsKey, speed]);

  const renderHalf = (isDuplicate) =>
    Array.from({ length: loop.sets }, (_, setIndex) => {
      const isReadable = !isDuplicate && setIndex === 0;
      return (
        <span
          key={`${isDuplicate ? "b" : "a"}-${setIndex}`}
          ref={isReadable ? setRef : undefined}
          aria-hidden={isReadable ? undefined : "true"}
          className="flex shrink-0"
        >
          {items.map((item, itemIndex) => (
            <span key={itemIndex} className={ITEM_CLASS}>
              {item}
            </span>
          ))}
        </span>
      );
    });

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden">
      <div
        className={`flex w-max [--marquee-shift:-50%] rtl:[--marquee-shift:50%] motion-reduce:animate-none ${
          pauseOnHover ? PAUSE_CLASS : ""
        } ${loop.durationS ? "animate-header-banner-marquee" : ""}`}
        style={{ animationDuration: `${loop.durationS}s` }}
      >
        {renderHalf(false)}
        {renderHalf(true)}
      </div>
    </div>
  );
}
